import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Star, Pencil, Layers } from "lucide-react";
import { resolveImg } from "../lib/api";

/**
 * A product in a grid. The whole card is one link to the product page.
 *
 * It used to carry an "add" button and a "Pedir Informação" link. The add
 * button could not do its job here: a product with models has to be chosen
 * before it can be added, so the button opened a dialog on top of the list.
 * Sending everyone to the page instead means they choose their model, read the
 * measurements and see the other photographs before deciding - and it leaves
 * room for the price, which was wrapping onto two lines beside the buttons.
 */
export const ProductCard = ({ product, index = 0 }) => {
  const img = resolveImg(product.images?.[0]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      data-testid={`product-card-${product.id}`}
      className="group relative bg-card rounded-[2rem] border border-border overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:shadow-[0_20px_50px_rgb(112,0,255,0.14)] transition-shadow duration-300 flex flex-col"
    >
      <Link to={`/produto/${product.id}`} data-testid={`product-info-${product.id}`}
        className="flex flex-col flex-1">
        <div className="relative aspect-[4/3] bg-muted overflow-hidden">
          {img ? (
            <>
              {/*
                Photographs arrive in every shape. Cropping them to fill the
                frame cut the piece in half whenever it was not dead centre, so
                the image is contained and a blurred, enlarged copy of itself
                fills what is left - the grid stays uniform and nothing is lost.
              */}
              <img src={img} alt="" aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50" />
              <img src={img} alt={product.name}
                className="relative w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
            </>
          ) : (
            <div className="w-full h-full grid place-items-center text-muted-foreground">Sem imagem</div>
          )}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {product.is_new && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground text-xs font-bold px-3 py-1 shadow"><Sparkles size={12}/> Novo</span>
            )}
            {product.is_featured && (
              <span className="inline-flex items-center gap-1 rounded-full brand-gradient text-white text-xs font-bold px-3 py-1 shadow"><Star size={12}/> Destaque</span>
            )}
            {product.is_customizable && (
              <span className="inline-flex items-center gap-1 rounded-full bg-foreground text-white text-xs font-bold px-3 py-1 shadow"><Pencil size={12}/> Personalizável</span>
            )}
            {product.variants?.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white text-foreground text-xs font-bold px-3 py-1 shadow"><Layers size={12}/> {product.variants.length} modelos</span>
            )}
          </div>
        </div>

        <div className="p-5 flex flex-col flex-1 text-center">
          <span className="text-xs font-semibold text-primary/80 uppercase tracking-wide">{product.category}</span>
          <h3 className="font-display font-bold text-lg mt-1 leading-tight">{product.name}</h3>
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{product.description}</p>
          {product.price && (
            <span data-testid={`product-price-${product.id}`}
              className="font-display font-extrabold text-lg mt-4 whitespace-nowrap">
              {product.price}
            </span>
          )}
        </div>
      </Link>
    </motion.div>
  );
};
